import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingState } from '../ui/LoadingState.js';
import { KVGrid } from '../ui/KVGrid.js';
import { KVRow } from '../ui/KVRow.js';
import { Field } from '../ui/Field.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';
import { Tabs } from '../ui/Tabs.js';
import { Icon } from '../Icon.js';
import { formatMoney } from '../../lib/money.js';
import { previewTotals } from '../../lib/billingTotals.js';
import { useInvoice, useUpdateInvoice, type EstimateLineItem } from '../../hooks/useBilling.js';
import { InvoiceOutreachTab } from './InvoiceOutreachTab.js';

interface LineItemForm {
  description: string;
  qty: string;
  unitPriceDollars: string;
}

const EMPTY_LINE: LineItemForm = { description: '', qty: '1', unitPriceDollars: '' };

type DetailTab = 'info' | 'outreach';

export interface InvoiceDetailProps {
  id: number;
}

// Editable while 'draft' (line items, tax rate, notes, due date) — mirrors
// EstimateDetail's edit UX. accountId/contactId/currency are frozen at
// conversion and never shown as editable here.
export function InvoiceDetail({ id }: InvoiceDetailProps) {
  const { t } = useTranslation();
  const { data: invoice, isLoading } = useInvoice(id);
  const updateInvoice = useUpdateInvoice();

  const [lines, setLines] = useState<LineItemForm[]>([{ ...EMPTY_LINE }]);
  const [taxPercent, setTaxPercent] = useState('0');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>('info');

  useEffect(() => {
    if (!invoice) return;
    const items = JSON.parse(invoice.lineItemsJson) as EstimateLineItem[];
    setLines(
      items.length
        ? items.map(li => ({ description: li.description, qty: String(li.qty), unitPriceDollars: (li.unitPrice / 100).toFixed(2) }))
        : [{ ...EMPTY_LINE }],
    );
    setTaxPercent((invoice.taxRate / 100).toString());
    setNotes(invoice.notes ?? '');
    setDueDate(invoice.dueDate ?? '');
  }, [invoice]);

  if (isLoading || !invoice) return <LoadingState size="sm" />;

  const isDraft = invoice.status === 'draft';
  const currency = invoice.currency;
  const preview = previewTotals(lines, taxPercent);

  function updateLine(idx: number, patch: Partial<LineItemForm>) {
    setLines(ls => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    setSaved(false);
  }

  function addLine() {
    setLines(ls => [...ls, { ...EMPTY_LINE }]);
  }

  function removeLine(idx: number) {
    setLines(ls => (ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls));
  }

  function handleSave() {
    updateInvoice.mutate({
      id,
      lineItems: lines
        .filter(l => l.description.trim())
        .map(l => ({
          description: l.description.trim(),
          qty: Math.max(0, Math.trunc(Number(l.qty) || 0)),
          unitPrice: Math.max(0, Math.round((Number(l.unitPriceDollars) || 0) * 100)),
        })),
      taxRate: Math.max(0, Math.round((Number(taxPercent) || 0) * 100)),
      notes: notes.trim() || null,
      dueDate: dueDate || null,
    }, { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); } });
  }

  return (
    <>
      <div
        className="shrink-0"
        style={{
          height: 'var(--evo-tab-row-h)',
          display: 'flex',
          alignItems: 'flex-end',
          marginBottom: 'var(--evo-space-4)',
          borderBottom: '1px solid var(--evo-stroke)',
        }}
      >
        <Tabs<DetailTab>
          tabs={[
            { value: 'info', label: t('billing.estimates.tabs.info') },
            { value: 'outreach', label: t('billing.estimates.tabs.outreach') },
          ]}
          value={activeTab}
          onChange={setActiveTab}
          size="sm"
          noBorder
        />
      </div>

      {activeTab === 'outreach' && (
        <InvoiceOutreachTab invoice={invoice} />
      )}

      {activeTab === 'info' && (
      <>
      {!isDraft && (
        <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.estimates.readOnlyHint')}</p>
      )}

      <Field label={t('billing.estimates.lineItems')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px 120px 32px', gap: 'var(--evo-space-2)', fontSize: '11px', color: 'var(--evo-fg-4)' }}>
            <span>{t('billing.estimates.description')}</span>
            <span>{t('billing.estimates.qty')}</span>
            <span>{t('billing.estimates.unitPrice')}</span>
            <span>{t('billing.estimates.amount')}</span>
            <span />
          </div>
          {lines.map((line, idx) => {
            const amount = Math.max(0, Math.trunc(Number(line.qty) || 0)) * Math.max(0, Math.round((Number(line.unitPriceDollars) || 0) * 100));
            return (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px 120px 32px', gap: 'var(--evo-space-2)', alignItems: 'center' }}>
                <Input value={line.description} onChange={v => updateLine(idx, { description: v })} size="sm" fullWidth disabled={!isDraft} />
                <Input type="number" value={line.qty} onChange={v => updateLine(idx, { qty: v })} size="sm" fullWidth disabled={!isDraft} />
                <Input type="number" value={line.unitPriceDollars} onChange={v => updateLine(idx, { unitPriceDollars: v })} size="sm" fullWidth disabled={!isDraft} />
                <span className="text-sm" style={{ color: 'var(--evo-fg-2)' }}>{formatMoney(amount, currency)}</span>
                {isDraft && (
                  <IconButton icon={<Icon name="close" size={14} />} size="sm" tone="muted" onClick={() => removeLine(idx)} ariaLabel={t('billing.estimates.removeLine')} />
                )}
              </div>
            );
          })}
          {isDraft && (
            <Button variant="outline" tone="muted" size="sm" onClick={addLine}>
              {t('billing.estimates.addLine')}
            </Button>
          )}
        </div>
      </Field>

      <Field label={t('billing.estimates.taxRatePercent')}>
        <Input type="number" value={taxPercent} onChange={setTaxPercent} size="sm" disabled={!isDraft} />
      </Field>

      <Field label={t('billing.estimates.notes')}>
        <Textarea value={notes} onChange={setNotes} rows={2} fullWidth disabled={!isDraft} />
      </Field>

      <Field label={t('billing.facturacion.dueDate')}>
        <Input type="date" value={dueDate} onChange={setDueDate} size="sm" disabled={!isDraft} />
      </Field>

      <KVGrid columns={2}>
        <KVRow label={t('billing.estimates.subtotal')} value={formatMoney(preview.subtotal, currency)} />
        <KVRow label={t('billing.estimates.tax')} value={formatMoney(preview.taxAmount, currency)} />
        <KVRow label={t('billing.estimates.total')} value={formatMoney(preview.total, currency)} last />
      </KVGrid>

      {isDraft && (
        <div style={{ marginTop: 'var(--evo-space-4)' }}>
          <Button
            tone="cyan"
            variant={saved ? 'outline' : 'solid'}
            size="sm"
            loading={updateInvoice.isPending}
            onClick={handleSave}
          >
            {updateInvoice.isPending ? t('billing.estimates.saving') : saved ? t('billing.estimates.saved') : t('billing.estimates.save')}
          </Button>
        </div>
      )}
      </>
      )}
    </>
  );
}
